import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Copy, Loader2, Swords, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { SignInRequired } from "@/components/SignInRequired";

const TITLE = "Study Friends & Challenges - Inquira";
const DESC = "See which friends are studying right now, follow their milestones and league moves, and start friendly study challenges.";

export const Route = createFileRoute("/friends")({
  component: FriendsPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

interface Friend {
  user_id: string;
  name: string;
  streak: number;
  level: number;
  week_xp: number;
  league_xp: number;
  status: string;
  activity: string | null;
  last_seen_at: string | null;
}
interface Challenge {
  id: string;
  metric: string;
  target: number;
  status: string;
  ends_at: string;
  i_am_challenger: boolean;
  my_progress: number;
  their_progress: number;
  their_name: string;
}
interface Intel {
  my_code: string | null;
  my_week_xp: number;
  pending: Array<{ id: string; name: string }>;
  outgoing: Array<{ id: string; name: string }>;
  friends: Friend[];
  challenges: Challenge[];
}

type Rpc = (name: string, args?: object) => Promise<{ data: unknown; error: { message: string } | null }>;

const ACTIVITY_LABEL: Record<string, string> = {
  problem_solved: "solving a problem",
  quiz_completed: "taking a quiz",
  flashcard_reviewed: "reviewing flashcards",
  practice_completed: "practising",
  study_session: "in a study session",
  plan_task_completed: "working through a plan",
};

/** Encouraging, never shaming: we only ever compare upward as an invitation. */
/** The RPC can return an error envelope (e.g. an expired session); never let that crash the page. */
function normalizeIntel(data: unknown): Intel {
  const d = (data ?? {}) as Partial<Intel>;
  return {
    my_code: d.my_code ?? null,
    my_week_xp: Number(d.my_week_xp ?? 0),
    pending: Array.isArray(d.pending) ? d.pending : [],
    outgoing: Array.isArray(d.outgoing) ? d.outgoing : [],
    friends: Array.isArray(d.friends) ? d.friends : [],
    challenges: Array.isArray(d.challenges) ? d.challenges : [],
  };
}

function motivation(me: number, f: Friend): string {
  if (f.week_xp > me && me > 0) return `${f.name} is ${f.week_xp - me} XP ahead this week — catchable today.`;
  if (f.week_xp > me) return `${f.name} has a head start this week. One session puts you on the board.`;
  if (me > f.week_xp && f.week_xp > 0) return `You're leading by ${me - f.week_xp} XP — send some encouragement.`;
  if (f.streak >= 3) return `${f.streak}-day streak going strong. Cheer them on.`;
  return "You two are neck and neck this week.";
}

function FriendsPage() {
  const { user, isLoading } = useAuth();
  const [intel, setIntel] = useState<Intel | null>(null);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  // Call through the client so `this` stays bound (the client is a lazy Proxy).
  const rpc = useCallback<Rpc>((name, args) => (supabase.rpc as unknown as Rpc).call(supabase, name, args), []);

  const load = useCallback(async () => {
    const { data, error } = await rpc("get_friend_intel");
    setLoading(false);
    if (error) { toast.error("Could not load your study friends. Please try again."); return; }
    const envelope = data as { error?: string } | null;
    if (envelope?.error) {
      toast.error(envelope.error === "unauthenticated" ? "Your session expired — please sign in again." : "Could not load your study friends.");
    }
    setIntel(normalizeIntel(data));
  }, [rpc]);

  useEffect(() => { if (user) load(); else setLoading(false); }, [user, load]);

  // Keep presence fresh while this page is open so friends see "online".
  useEffect(() => {
    if (!user) return;
    const ping = () => { void rpc("touch_presence", { _activity: "online" }); };
    ping();
    // Skip pings while the tab is in the background — nobody is studying there.
    const t = setInterval(() => {
      if (document.hidden) return;
      ping();
    }, 180_000);
    return () => clearInterval(t);
  }, [user, rpc]);

  const add = async () => {
    if (!code.trim()) return;
    setBusy(true);
    const { data } = await rpc("send_friend_request", { _code: code.trim() });
    setBusy(false);
    const res = data as { ok?: boolean; error?: string };
    if (res?.ok) { toast.success("Request sent."); setCode(""); load(); }
    else toast.error(res?.error === "no_such_user" ? "No one uses that code." : res?.error === "already_exists" ? "You're already connected." : "Could not send that request.");
  };

  const respond = async (id: string, accept: boolean) => {
    await rpc("respond_friend_request", { _id: id, _accept: accept });
    load();
  };

  const challenge = async (friendId: string) => {
    const { data } = await rpc("create_friend_challenge", { _friend_id: friendId, _metric: "xp", _target: 300, _days: 7 });
    const res = data as { ok?: boolean };
    if (res?.ok) toast.success("Challenge sent: first to 300 XP in 7 days.");
    else toast.error("Could not start that challenge.");
    load();
  };

  const respondChallenge = async (id: string, accept: boolean) => {
    await rpc("respond_friend_challenge", { _id: id, _accept: accept });
    load();
  };

  if (isLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="size-6 animate-spin text-ink-muted" /></div>;
  }
  if (!user) return <SignInRequired title="Sign in to see your study friends" description="Friend intelligence shows who is studying and how your week compares." />;

  return (
    <div className="min-h-screen max-w-3xl mx-auto px-4 py-6 space-y-6">
      <Link to="/" className="flex items-center gap-2 text-sm text-ink-muted hover:text-ink">
        <ArrowLeft className="size-4" /> Home
      </Link>

      <header>
        <h1 className="text-3xl font-display font-black text-ink">Study friends</h1>
        <p className="text-sm text-ink-muted mt-1">Who's studying, how everyone's week is going, and friendly challenges. No shaming, ever.</p>
      </header>

      <section className="rounded-2xl border border-rule bg-card p-4 space-y-3">
        <h2 className="font-bold text-ink flex items-center gap-2"><UserPlus className="size-4 text-neon-cyan" /> Add a friend</h2>
        <div className="flex items-center gap-2">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Their friend code"
            aria-label="Friend code"
            className="flex-1 rounded-xl border border-rule bg-transparent px-3 py-2 text-sm text-ink"
          />
          <button onClick={add} disabled={busy} className="rounded-xl bg-primary text-primary-foreground px-4 py-2 text-sm font-bold disabled:opacity-50">
            Send
          </button>
        </div>
        {intel?.my_code && (
          <button
            onClick={() => { navigator.clipboard.writeText(intel.my_code!); toast.success("Your code is copied."); }}
            className="text-xs text-ink-muted flex items-center gap-1.5 hover:text-ink"
          >
            <Copy className="size-3.5" /> Your code: <span className="font-mono font-bold text-ink">{intel.my_code}</span>
          </button>
        )}
      </section>

      {!!intel?.pending.length && (
        <section className="rounded-2xl border border-rule bg-card p-4 space-y-2">
          <h2 className="font-bold text-ink">Requests</h2>
          {intel.pending.map((p) => (
            <div key={p.id} className="flex items-center justify-between text-sm">
              <span className="text-ink">{p.name}</span>
              <div className="flex gap-2">
                <button onClick={() => respond(p.id, true)} className="rounded-full px-3 py-1 text-xs font-bold bg-primary text-primary-foreground">Accept</button>
                <button onClick={() => respond(p.id, false)} className="rounded-full px-3 py-1 text-xs font-bold border border-rule text-ink-muted">Decline</button>
              </div>
            </div>
          ))}
        </section>
      )}

      <section className="rounded-2xl border border-rule bg-card p-4 space-y-3">
        <h2 className="font-bold text-ink flex items-center gap-2"><Users className="size-4 text-neon-cyan" /> Friends</h2>
        {intel?.friends.length ? intel.friends.map((f) => (
          <div key={f.user_id} className="rounded-xl border border-rule p-3 space-y-1">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-ink flex items-center gap-2">
                <span className={`size-2 rounded-full ${f.status === "studying" ? "bg-neon-yellow" : f.status === "offline" ? "bg-rule" : "bg-neon-cyan"}`} />
                {f.name}
              </span>
              <button onClick={() => challenge(f.user_id)} className="text-xs font-bold rounded-full px-3 py-1 border border-rule text-ink hover:bg-rule/30 flex items-center gap-1">
                <Swords className="size-3.5" /> Challenge
              </button>
            </div>
            <p className="text-xs text-ink-muted">
              {f.status === "studying"
                ? `Studying now — ${ACTIVITY_LABEL[f.activity ?? ""] ?? "in the app"}`
                : f.status === "online" ? "Online now"
                : f.last_seen_at ? `Last active ${new Date(f.last_seen_at).toLocaleDateString()}` : "Not active yet"}
            </p>
            <p className="text-xs text-ink-muted">
              Level {f.level} · {f.streak}-day streak · {f.week_xp} XP this week · {f.league_xp} league XP
            </p>
            <p className="text-xs text-ink">{motivation(intel?.my_week_xp ?? 0, f)}</p>
          </div>
        )) : (
          <p className="text-sm text-ink-muted">No friends yet — share your code above to connect.</p>
        )}
      </section>

      <section className="rounded-2xl border border-rule bg-card p-4 space-y-3">
        <h2 className="font-bold text-ink flex items-center gap-2"><Swords className="size-4 text-neon-pink" /> Challenges</h2>
        {intel?.challenges.length ? intel.challenges.map((c) => (
          <div key={c.id} className="rounded-xl border border-rule p-3 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-ink">vs {c.their_name}</span>
              <span className="text-xs text-ink-muted capitalize">{c.status}</span>
            </div>
            <p className="text-xs text-ink-muted">
              First to {Math.round(c.target)} {c.metric} · ends {new Date(c.ends_at).toLocaleDateString()}
            </p>
            <p className="text-xs text-ink">You {Math.round(c.my_progress)} — {Math.round(c.their_progress)} them</p>
            {c.status === "pending" && !c.i_am_challenger && (
              <div className="flex gap-2 pt-1">
                <button onClick={() => respondChallenge(c.id, true)} className="rounded-full px-3 py-1 text-xs font-bold bg-primary text-primary-foreground">Accept</button>
                <button onClick={() => respondChallenge(c.id, false)} className="rounded-full px-3 py-1 text-xs font-bold border border-rule text-ink-muted">Decline</button>
              </div>
            )}
          </div>
        )) : (
          <p className="text-sm text-ink-muted">No challenges yet. Start one from a friend above.</p>
        )}
      </section>
    </div>
  );
}